import { useEffect } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import type { TProfile, TRequest } from "@shared/entities";
import { useUseTemplateModal } from "./useUseTemplateModal";
import { TemplateStep1 } from "./TemplateStep1";
import { TemplateStep2 } from "./TemplateStep2";
import { TemplateStepPreview } from "./TemplateStepPreview";
import { TemplateStep3 } from "./TemplateStep3";

type Props = {
  requestId: string;
  profile?: TProfile;
  request: TRequest;
  onSuccess: () => void;
  onClose: () => void;
};

const STEP_TITLE_KEYS: Record<1 | 2 | 3 | 4, string> = {
  1: "step1Title",
  2: "step2Title",
  3: "stepPreviewTitle",
  4: "step4Title",
};

export function UseTemplateModal({ requestId, profile, request, onSuccess, onClose }: Props) {
  const { t } = useTranslation("doctorTemplates");

  const state = useUseTemplateModal({ requestId, profile, request, onSuccess, onClose });

  useEffect(() => {
    state.loadTemplates();
  }, [state.loadTemplates]);

  const step = state.step as 1 | 2 | 3 | 4;

  return (
    <Modal isOpen onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{t(STEP_TITLE_KEYS[step] as any)}</ModalHeader>
            <ModalBody>
              {step === 1 && (
                <TemplateStep1
                  templates={state.templates}
                  templatesLoading={state.templatesLoading}
                  selectedTemplate={state.selectedTemplate}
                  onSelect={state.handleSelectTemplate}
                />
              )}
              {step === 2 && state.selectedTemplate && (
                <TemplateStep2
                  content={state.editedContent ?? state.selectedTemplate.content}
                  title={state.title}
                  variableKeys={state.variableKeys}
                  onReady={state.handleEditorReady}
                  onChange={state.handleContentChange}
                  onTitleChange={state.setTitle}
                />
              )}
              {step === 3 && (state.editedContent ?? state.selectedTemplate?.content) && (
                <TemplateStepPreview
                  title={state.title}
                  content={(state.editedContent ?? state.selectedTemplate?.content)!}
                  variableValues={state.variableValues}
                />
              )}
              {step === 4 && (
                <TemplateStep3
                  signaturePosition={state.signaturePosition}
                  onPositionChange={state.setSignaturePosition}
                  error={state.error}
                />
              )}
              {step === 3 && state.error && (
                <div className="rounded-lg bg-danger-50 px-4 py-2 text-sm text-danger">
                  {state.error}
                </div>
              )}
            </ModalBody>
            <ModalFooter className="flex justify-between">
              <Button variant="light" onPress={onClose}>
                {t("cancel")}
              </Button>
              <div className="flex gap-2">
                {step > 1 && (
                  <Button variant="bordered" onPress={state.goBack} isDisabled={state.generating}>
                    {t("back")}
                  </Button>
                )}
                {step < 4 && (
                  <Button
                    color="primary"
                    onPress={step === 1 ? state.goToStep2 : step === 2 ? state.goToPreview : state.goToStep4}
                    isDisabled={step === 1 ? !state.selectedTemplate : false}
                  >
                    {t("next")}
                  </Button>
                )}
                {step === 4 && (
                  <Button
                    color="primary"
                    onPress={state.handleGenerate}
                    isDisabled={state.generating}
                    startContent={state.generating ? <Spinner size="sm" /> : undefined}
                  >
                    {state.generating ? t("generating") : t("generate")}
                  </Button>
                )}
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
